// src/lib/kpi.js
// STATUS: active | UPDATED: 2026-09-23
// LOG:
//   2026-09-23  New. The fourth leg of the governing test, finally wired.
//               Date and growth math lives in kpiMath.js so it can be tested.
//
// THE NUMBERS A STORE REPORTS, WHICH ARE JAZZ'S AND NOT OURS.
//
// Page 19 of the Developer Context says every feature must help an employee
// learn the process, help them practice it, help a manager inspect it, or help
// the dealership MEASURE AND SUSTAIN it. The fourth one has never had a screen.
//
// `kpi_definitions` was empty until 2026-09-23 because seeding it before Jazz
// answered would have been inventing his business for him. He answered on
// 2026-08-30 and 0037 finally applied it. Five numbers, his:
//
//   "the total units or vehicles sold each month. the total gross profit for
//    the sales, finance, service and parts departments. If a store can increase
//    these metrics by at least 10 - 20% each month, that would look good."
//
// WHY GROWTH RATHER THAN A TARGET. `kpi_definitions.target_value` is null on
// every row on purpose, and 0037 explains it at length: his bar is 10 to 20
// percent month over month, which is a property of the CHANGE between two
// entries rather than of any single one, and `unit` is dollars or count. A 10
// in a dollars target column reads as ten dollars. So the bar lives in
// kpiMath.js, applied to a computed delta.
//
// WHO MAY WRITE. The owner, their own store, enforced by RLS in 0038 rather
// than by this file. Before that migration any salesperson at the store could
// have zeroed these.

import { supabase } from './supabase'
import { KPI_ORDER, parseValue } from './kpiMath'

export * from './kpiMath'

export async function listDefinitions() {
  const { data, error } = await supabase
    .from('kpi_definitions')
    .select('id, slug, name, department, unit, description')
  if (error) return { data: [], error }
  const rank = (s) => {
    const i = KPI_ORDER.indexOf(s)
    return i === -1 ? 99 : i
  }
  return { data: [...(data || [])].sort((a, b) => rank(a.slug) - rank(b.slug)), error: null }
}

// Every entry from `since` forward. Small table by design: one store, one KPI,
// one month, so a year of a five metric store is sixty rows.
export async function listEntries(dealershipId, since) {
  if (!dealershipId) return { data: [], error: null }
  const { data, error } = await supabase
    .from('kpi_entries')
    .select('id, kpi_definition_id, period_month, value, notes')
    .eq('dealership_id', dealershipId)
    .gte('period_month', since)
    .order('period_month')
  return { data: data || [], error }
}

// UPSERT ON THE NATURAL KEY, which already had a unique constraint from 0003:
// (dealership_id, kpi_definition_id, period_month). Without it a second save of
// the same month would sit beside the first and every figure on the screen
// would silently double.
//
// A BLANK FIELD DELETES rather than writing zero. Zero units in a month is a
// real and terrible number somebody might genuinely report, and it must not be
// what "I do not have this yet" looks like.
export async function saveMonth({ dealershipId, periodMonth, values, userId }) {
  if (!dealershipId) return { error: 'No dealership on your account.' }
  const rows = []
  const clear = []
  for (const [definitionId, raw] of Object.entries(values || {})) {
    const value = parseValue(raw)
    if (value === null) {
      clear.push(definitionId)
      continue
    }
    if (!Number.isFinite(value) || value < 0) return { error: 'Numbers only, and none of them below zero.' }
    rows.push({
      dealership_id: dealershipId,
      kpi_definition_id: definitionId,
      period_month: periodMonth,
      value,
      entered_by: userId || null,
    })
  }

  if (clear.length > 0) {
    const { error } = await supabase
      .from('kpi_entries')
      .delete()
      .eq('dealership_id', dealershipId)
      .eq('period_month', periodMonth)
      .in('kpi_definition_id', clear)
    if (error) return { error: error.message || String(error) }
  }

  if (rows.length > 0) {
    const { error } = await supabase
      .from('kpi_entries')
      .upsert(rows, { onConflict: 'dealership_id,kpi_definition_id,period_month' })
    if (error) return { error: error.message || String(error) }
  }

  return { error: null }
}
